import {
  LineupConfig as Config,
  isPlayerSame,
  LineupModel,
  LineupPlayer,
  toSortedByLineupOrder,
  updateLineups,
} from '@collector/desktop-feature-lineups'
import { compareParticipantByLineupOrder } from '@collector/shared-ui-domain'
import {
  ParticipantSubType,
  SubParticipantPosition,
} from '@collector/sportsapi-client-legacy'
import { Event, Participant } from '@collector/sportsapi-types'
import { sportsApiClient } from '@desktop/globalState/sportsApiClient'

import { LineupConfig } from '../../configuration'
import {
  BaseballLineupModel,
  createBaseballLineupModel,
  getBaseballSquadPayload,
  PositionKey,
} from './lineupConfig.utils'
import {
  toSortedByPitcherOrder,
  transformBaseballSubParticipants,
} from './pitcherOrder'

export type { BaseballLineupModel, PositionKey } from './lineupConfig.utils'
export type BaseballLineupConfig = Config<PositionKey>

export function hasPositionChanged(
  key: PropertyKey,
  lineup: LineupModel,
  initialLineup: LineupModel,
): boolean {
  const current = lineup[key]
  const initial = initialLineup[key]

  if (current.length !== initial.length) return true

  for (let i = 0; i < current.length; i++) {
    const playerA: LineupPlayer = current[i]
    const playerB: LineupPlayer = initial[i]

    const samePlayer = isPlayerSame(playerA, playerB)
    const sameShirtNr = playerA.shirt_nr === playerB.shirt_nr
    const sameSecondaryPosition =
      !!playerA.has_secondary_position === !!playerB.has_secondary_position

    const same = samePlayer && sameShirtNr && sameSecondaryPosition

    if (!same) return true
  }

  return false
}

function syncPositions(
  lineup: LineupModel,
  updatedKey: PropertyKey,
  player: LineupPlayer,
): BaseballLineupModel {
  const bl = lineup as BaseballLineupModel

  if (updatedKey === 'pitcher') {
    const stillInPitchers = bl.pitcher.some((p) => isPlayerSame(p, player))

    // Determine the desired state of the pitcher-batter clone.
    // Force position to Pitcher on the clone so that `getPlayerConfig` in
    // the batter section always identifies pitcher-batters correctly,
    // regardless of the player's original squad position.
    const clone = { ...player, position: SubParticipantPosition.Pitcher }
    const existsInBatter = bl.batter.some((b) => isPlayerSame(b, player))
    const shouldBeInBatter = stillInPitchers && player.has_secondary_position

    // Three cases:
    // 1. Already in batter + should stay  → replace in place to preserve batting order
    // 2. Already in batter + should leave → filter out
    // 3. Not in batter   + should be added → append to end
    const batter = existsInBatter
      ? shouldBeInBatter
        ? bl.batter.map((b) => (isPlayerSame(b, player) ? clone : b))
        : bl.batter.filter((b) => !isPlayerSame(b, player))
      : shouldBeInBatter
        ? [...bl.batter, clone]
        : bl.batter

    return { ...bl, batter }
  }

  if (updatedKey === 'batter') {
    // Reflect has_secondary_position changes back to the pitcher entry.
    const pitcher = bl.pitcher.map((p) =>
      isPlayerSame(p, player)
        ? { ...p, has_secondary_position: player.has_secondary_position }
        : p,
    )
    return { ...bl, pitcher }
  }

  return bl
}

export const lineupConfig: LineupConfig = {
  config: {
    syncPositions,
    positions: {
      batter: {
        sectionLabel: 'Batter',
        buttonLabel: 'BAT',
        showShirtNr: true,
        addFoundPlayer: {
          disableForSubTypes: [ParticipantSubType.Coach],
        },
        sortable: true,
        secondaryPosition: 'PIT',
        getPlayerConfig: (player) => {
          if (player.position === SubParticipantPosition.Pitcher) {
            return {
              isShirtNrInputEnabled: false,
              showDeleteButton: false,
              secondaryPosition: undefined,
            }
          }
          return {}
        },
      },
      pitcher: {
        sectionLabel: 'Pitcher',
        buttonLabel: 'PIT',
        showShirtNr: true,
        addFoundPlayer: {
          disableForSubTypes: [ParticipantSubType.Coach],
        },
        sortable: true,
        secondaryPosition: 'BAT',
      },
      coach: {
        sectionLabel: 'Coach',
        buttonLabel: 'COA',
        maxCount: 1,
        maxCountReasonText: 'Coach is already added',
        buttonClass: 'hover:bg-neutral-light-6 hover:border-neutral-dark-0',
      },
      willNotPlay: {
        deleteOnly: true,
        sectionLabel: 'Will not play',
        buttonLabel: '',
        buttonClass: 'hover:bg-neutral-light-6 hover:border-neutral-dark-0',
      },
    },
    triggerSortOnLineupModelUpdate: false,
  },
  createLineupModel: createBaseballLineupModel,
  toSortedLineupModel(lineup: LineupModel<PositionKey>): BaseballLineupModel {
    return {
      batter: toSortedByLineupOrder(lineup.batter),
      pitcher: toSortedByPitcherOrder(lineup.pitcher),
      coach: [...lineup.coach],
      willNotPlay: [...lineup.willNotPlay],
    }
  },
  async updateLineups(
    eventId: Event['id'],
    participantId: Participant['id'],
    lineup: LineupModel<PositionKey>,
  ): Promise<void> {
    const bl = lineup as BaseballLineupModel
    const squad = getBaseballSquadPayload(bl)

    await updateLineups(sportsApiClient, eventId, participantId, squad)
  },
  hasPositionChanged,
  sortSquad: compareParticipantByLineupOrder,
  transformSubParticipants: transformBaseballSubParticipants,
}
